import { Image, Tag } from "antd";
import {
  DownloadOutlined,
  FileExcelOutlined,
  FileImageOutlined,
  FileOutlined,
  FilePdfOutlined,
  FilePptOutlined,
  FileTextOutlined,
  FileWordOutlined,
  FileZipOutlined,
  PaperClipOutlined,
} from "@ant-design/icons";
import type { Attachment } from "../api/threads";
import { apiUrl } from "../api/client";

function humanSize(bytes: number): string {
  if (!bytes || bytes < 1024) return `${bytes || 0} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

function iconFor(mime: string, filename: string): React.ReactNode {
  const ext = filename.split(".").pop()?.toLowerCase() ?? "";
  if (mime.startsWith("image/")) return <FileImageOutlined />;
  if (mime === "application/pdf" || ext === "pdf") return <FilePdfOutlined />;
  if (/excel|spreadsheet|sheet/.test(mime) || /xlsx?|csv|numbers/.test(ext))
    return <FileExcelOutlined />;
  if (/word|document/.test(mime) || /docx?|pages/.test(ext)) return <FileWordOutlined />;
  if (/presentation|powerpoint/.test(mime) || /pptx?|keynote/.test(ext))
    return <FilePptOutlined />;
  if (/zip|compressed|gzip|tar|7z/.test(mime) || /zip|gz|tar|7z|rar/.test(ext))
    return <FileZipOutlined />;
  if (mime.startsWith("text/")) return <FileTextOutlined />;
  return <FileOutlined />;
}

// 发件方（尤其微信转发）常把图片的 Content-Type 错标成 application/pdf 或
// application/octet-stream，Gmail 原样透传。这里用扩展名兜底，避免图片掉进下载块。
const IMAGE_EXT = /\.(png|jpe?g|gif|webp|bmp|svg|heic|heif|avif|tiff?|ico)$/i;
function isImage(a: Attachment): boolean {
  return a.mimeType.startsWith("image/") || IMAGE_EXT.test(a.filename);
}

function urlOf(messageId: string, a: Attachment): string {
  return apiUrl(`/messages/${messageId}/attachments/${a.attachmentId}`);
}

async function downloadAttachment(messageId: string, a: Attachment): Promise<void> {
  const res = await fetch(urlOf(messageId, a), { credentials: "include" });
  if (!res.ok) throw new Error("download_failed");
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = a.filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

export function AttachmentChips({
  messageId,
  attachments,
  showInline = false,
}: {
  messageId: string;
  attachments: Attachment[];
  // Inline images are normally rendered inside the HTML body via cid: refs, so
  // they're hidden here. Set showInline for plain-text bodies (e.g. case view)
  // where they would otherwise be invisible.
  showInline?: boolean;
}) {
  const visible = attachments.filter((a) => showInline || !a.inline);
  if (visible.length === 0) return null;
  const images = visible.filter((a) => isImage(a));
  const others = visible.filter((a) => !isImage(a));

  return (
    <div
      style={{
        marginTop: 10,
        paddingTop: 10,
        borderTop: "1px dashed var(--ant-color-border)",
      }}
    >
      <div
        style={{
          fontSize: 14,
          color: "var(--ant-color-text-secondary)",
          marginBottom: 6,
          display: "inline-flex",
          alignItems: "center",
          gap: 4,
        }}
      >
        <PaperClipOutlined /> {visible.length} 个附件
      </div>

      {images.length > 0 ? (
        <div style={{ marginBottom: 8 }}>
          <Image.PreviewGroup>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
              {images.map((a) => (
                <div
                  key={a.attachmentId}
                  style={{
                    width: 96,
                    height: 96,
                    borderRadius: 8,
                    overflow: "hidden",
                    border: "1px solid var(--ant-color-border)",
                    background: "var(--ant-color-bg-layout)",
                  }}
                >
                  <Image
                    src={urlOf(messageId, a)}
                    alt={a.filename}
                    width={96}
                    height={96}
                    style={{ objectFit: "cover" }}
                    preview={{ mask: "预览" }}
                  />
                </div>
              ))}
            </div>
          </Image.PreviewGroup>
        </div>
      ) : null}

      {others.length > 0 ? (
        <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
          {others.map((a) => (
            <Tag.CheckableTag
              key={a.attachmentId}
              checked={false}
              onChange={() => {
                void downloadAttachment(messageId, a).catch(() => null);
              }}
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
                padding: "6px 10px",
                background: "var(--ant-color-bg-layout)",
                border: "1px solid var(--ant-color-border)",
                color: "var(--ant-color-text)",
                fontSize: 14,
                cursor: "pointer",
                borderRadius: 8,
                maxWidth: 280,
              }}
            >
              {iconFor(a.mimeType, a.filename)}
              <span
                style={{
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  flex: 1,
                  minWidth: 0,
                }}
              >
                {a.filename}
              </span>
              <span style={{ color: "var(--ant-color-text-secondary)" }}>
                {humanSize(a.size)}
              </span>
              <DownloadOutlined style={{ color: "var(--ant-color-text-secondary)" }} />
            </Tag.CheckableTag>
          ))}
        </div>
      ) : null}
    </div>
  );
}
